import { QuestionsService } from '@APP/services/questions-service';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { Events } from '../../events/app-events';
import { DepInjMixin, requires } from '../../mixins/dependency';
import { UserService } from '../../services/user-service';
import { eventBus } from '../../utils/event-bus';
import { BlackWhatsappIcon, WhatsappIcon } from '../icons/icons';
import { DeviceAwareComponent } from './device-aware-component';
import { ConfigService } from '@APP/services/config-service';
import { PopupService } from '@APP/services/popup-service';

type Deps = {
  userService: UserService;
  configService: ConfigService;
  questionsService: QuestionsService;
  popupService: QuestionsService;
};

@requires(UserService.serviceName, ConfigService.serviceName, QuestionsService.serviceName, PopupService.serviceName)
@customElement('lh-header-line')
export class LHHeaderLine extends DepInjMixin<Deps>(DeviceAwareComponent) {
  private unsubs: Function[] = [];

  @state()
    username: string = '';

  @state()
    agent: string = '';

  @query('#redirect-link')
    link!: HTMLElement;

  @property({ type: Boolean, attribute: 'black', reflect: true })
    black: boolean = false;

  static get baseStyles() {
    return [
      css`
        :host-context([keyboard-open]) #wrapper { height: 0; }
        :host(.section-changing) #sections-wrapper {
          user-select: none;
        }
      #wrapper {
        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 2.75rem;
        height: 3rem;
        max-height: calc(var(--current-header-height) + 0.5rem);
        margin-bottom: 2rem;
        transition: var(--transition-speed, 350ms) var(--transition-switch);
        transition-property: height;
        cursor:pointer;
      }
      #whatsapp-wrapper {
        width: 3rem;
        height: 3rem;
        position: absolute;
        right: 3.22rem;
      }
      #agent, #username {
        font-weight: bold;
        font-size: 3rem;
        color: var(--c-emphasize-sec);
      }
      #wrapper > a + span {
        margin-top: -1.5rem;
      }
      .icon-whatsapp {
        height: 3rem;
        width: 3rem;
      }
    `,
    ];
  };
  static get desktopStyles() {
    return [
      css`
    #whatsapp-wrapper {
      display: none !important;
    }
    #wrapper {
      height: 18rem;
      flex-direction: column;
    }
    `,
    ];
  };
  static get mobileStyles() {
    return [
      css`
    #wrapper {
      height: calc(var(--current-header-height) - 12rem);
     }
    :host([current-section="S_Welcome"]) #wrapper { 
      height: calc(var(--current-header-height) - 10rem); 
      filter: unset;
    }

    #agent, #username {
      margin-top: -1.5rem;
      font-size: 2.5rem;
    }
    #agent {
      margin-right: 0.3em;
    }
    #whatsapp-wrapper{
      display: none;
    }
    `,
    ];
  };

  connectedCallback() {
    super.connectedCallback();
    this.unsubs.push(
      eventBus().on<void>(Events.Type.Refresh, this.checkLoginState.bind(this)),
      eventBus().on(Events.Type.SectionChanged, (section) => this.setAttribute('current-section', section.sectionId)),
    );
    this.checkLoginState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubs.forEach((f) => f());
  }

  renderContent() {
    const { questionsService } = this.deps;
    const affiliate = questionsService.getAffiliateValue();
    return html`
      <div id="wrapper" dir="ltr">
        <span id="whatsapp-wrapper" @click=${this.handleWhatsappClick}>
          ${this.black ? BlackWhatsappIcon : WhatsappIcon}
        </span>
          <lh-logo @click=${questionsService.showBeforeExitPopup} .affiliate=${affiliate}></lh-logo>
          ${this.agent ? html`<span id="agent">${this.agent}</span>` : nothing}
          ${affiliate ?'' :html`<span id="username">${this.username}</span>`}
      </div>
    `;
  }

  private handleWhatsappClick() {
    window.open('https://wa.me/972775712722', '_blank');
  }

  private checkLoginState() {
    this.username = this.deps.userService.getNameTitle();
    this.agent = this.deps.userService.userType == 'agent' ? 'Internal Agent' : this.deps.userService.userType == 'externalAgent' ? 'Agent' : '';
  }
}
